// CreateProduct.tsx
import React, { useState, useEffect } from 'react';
import { CloudUpload, Tag, Percent, Scale, Ruler, Gem, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CreateProduct: React.FC = () => {
  const navigate = useNavigate();

  const [productName, setProductName] = useState('');
  const [materialType, setMaterialType] = useState('');
  const [gemstoneType, setGemstoneType] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [weight, setWeight] = useState('');
  const [gender, setGender] = useState('');
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [sku, setSku] = useState('');
  const [stockQuantity, setStockQuantity] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [currentTagInput, setCurrentTagInput] = useState('');

  const [makingCharges, setMakingCharges] = useState('');
  const [discount, setDiscount] = useState('');
  const [tax, setTax] = useState('');

  // **NEW STATE: To hold the actual file to be uploaded**
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState(''); // This will store the URL returned by the backend
  const [previewImage, setPreviewImage] = useState<string | null>(null); // For local preview

  const [selectedCity, setSelectedCity] = useState<string>('Delhi');
  
  // LIVE PRICE STATES (Backend se aayenge)
  const [liveGold24KPrice, setLiveGold24KPrice] = useState<number | null>(null);
  const [liveGold22KPrice, setLiveGold22KPrice] = useState<number | null>(null);
  const [liveGold18KPrice, setLiveGold18KPrice] = useState<number | null>(null);
  const [liveSilverPriceKg, setLiveSilverPriceKg] = useState<number | null>(null);

  const [calculatedFinalPrice, setCalculatedFinalPrice] = useState<number | null>(null);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [purity, setPurity] = useState('');

  const productCategories = [
    'Rings', 'Necklaces', 'Earrings', 'Bracelets', 'Pendants', 'Anklets',
    'Bangles', 'Chains', 'Mangalsutras', 'Nose Pins', 'Toe Rings', 'Watches',
    'Kids Jewelry', 'Mens Jewelry', 'Others'
  ];

  const indianCities = [
    'Delhi', 'Mumbai', 'Chennai', 'Kolkata', 'Hyderabad', 'Bengaluru',
    'Ahmedabad', 'Pune', 'Jaipur', 'Lucknow', 'Kanpur', 'Nagpur', 'Indore'
  ];

  const ringSizes = ['US 5', 'US 6', 'US 7', 'US 8', 'US 9', 'US 10'];
  const necklaceBraceletLengths = ['16 inch', '18 inch', '20 inch', 'Adjustable'];

  const formatCurrency = (amount: number | null) => {
    if (amount === null) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // BACKEND SE LIVE PRICE FETCH KARNE KA LOGIC
  useEffect(() => {
    const fetchMetalPrices = async () => {
      try {
        // const response = await fetch('http://localhost:8080/api/metal-prices');
        const response = await fetch('https://sardar-jewellers.com/api/metal-prices');
        if (response.ok) {
          const data = await response.json();
          // Backend se saare rates set kar rahe hain
          setLiveGold24KPrice(data.gold24K);
          setLiveGold22KPrice(data.gold22K);
          setLiveGold18KPrice(data.gold18K);
          setLiveSilverPriceKg(data.silverKg);
        } else {
          console.error("Failed to fetch live prices from backend");
        }
      } catch (error) {
        console.error("Error fetching live prices:", error);
      }
    };

    fetchMetalPrices();
  }, []); // Ab yeh sirf page load par fetch karega

  // PRICE CALCULATION LOGIC (Using backend rates)
  useEffect(() => {
    let baseMetalCost = 0;
    const parsedWeight = parseFloat(weight);
    const parsedMakingCharges = parseFloat(makingCharges) || 0;
    const parsedDiscount = parseFloat(discount) || 0;
    const parsedTax = parseFloat(tax) || 0;

    if (parsedWeight > 0 && materialType) {
      if (materialType === 'Gold' && purity) {
        // Direct admin wale rates calculate honge purity ke hisaab se
        if (purity === '24K' && liveGold24KPrice !== null) {
            baseMetalCost = liveGold24KPrice * parsedWeight;
        } else if (purity === '22K' && liveGold22KPrice !== null) {
            baseMetalCost = liveGold22KPrice * parsedWeight;
        } else if (purity === '18K' && liveGold18KPrice !== null) {
            baseMetalCost = liveGold18KPrice * parsedWeight;
        }
      } else if (materialType === 'Silver' && liveSilverPriceKg !== null) {
        // Silver ka rate admin 1KG ka deta hai, toh pehle per gram nikalenge
        const silverPricePerGram = liveSilverPriceKg / 1000;
        baseMetalCost = silverPricePerGram * parsedWeight;
      }

      // Agar data fetch nahi hua toh calculation aage na badhe
      if (baseMetalCost > 0) {
          let priceBeforeDiscount = baseMetalCost + parsedMakingCharges;
          let priceAfterDiscount = priceBeforeDiscount * (1 - (parsedDiscount / 100));
          let finalPrice = priceAfterDiscount * (1 + (parsedTax / 100));

          setCalculatedFinalPrice(parseFloat(finalPrice.toFixed(2)));
      } else {
          setCalculatedFinalPrice(null);
      }

    } else {
      setCalculatedFinalPrice(null);
    }
  }, [materialType, purity, weight, makingCharges, discount, tax, liveGold24KPrice, liveGold22KPrice, liveGold18KPrice, liveSilverPriceKg]);

  const handleSizeToggle = (size: string) => {
    setSelectedSizes((prev) =>
      prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size]
    );
  };

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && currentTagInput.trim() !== '') {
      setTags((prev) => [...prev, currentTagInput.trim()]);
      setCurrentTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags((prev) => prev.filter((tag) => tag !== tagToRemove));
  };

  const handleFileChangeForPreview = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSelectedFile(file); 
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedFile(null); 
      setPreviewImage(null);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      const file = event.dataTransfer.files[0];
      setSelectedFile(file); 
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setSelectedFile(null); 
      setPreviewImage(null);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append('files', file); 

    try {
      // const response = await fetch('http://localhost:8080/api/upload/images', {
      const response = await fetch('https://sardar-jewellers.com/api/upload/images', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Image upload failed: ${errorText}`);
      }

      const uploadedPaths: string[] = await response.json();
      if (uploadedPaths && uploadedPaths.length > 0) {
        return uploadedPaths[0]; 
      }
      return null;
    } catch (error) {
      console.error('Error uploading image:', error);
      setErrorMessage(`Error uploading image: ${(error as Error).message}`);
      return null;
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    setErrorMessage(null);

    if (!materialType || !productName || !weight || !productCategory || !stockQuantity || !purity || calculatedFinalPrice === null) {
      setErrorMessage('Please fill in all required product information fields and ensure price is calculated.');
      return;
    }
    if (materialType === 'Gold' && !gemstoneType) {
        setErrorMessage('Gemstone Type is required for Gold products.');
        return;
    }

    let finalImageUrl = imageUrl; 

    if (selectedFile) {
      const uploadedPath = await uploadImage(selectedFile);
      if (uploadedPath) {
        // finalImageUrl = `http://localhost:8080${uploadedPath}`; 
        finalImageUrl = `https://sardar-jewellers.com${uploadedPath}`; 
      } else {
        setErrorMessage("Image upload failed. Product not created.");
        return;
      }
    } else if (!imageUrl) {
        setErrorMessage("Please upload an image or provide an Image URL.");
        return;
    }

    const generatedItemId = Math.floor(100000 + Math.random() * 900000);
    const generatedItemCode = `${materialType.toUpperCase().substring(0,1)}-${generatedItemId}`;

    const commonProductData = {
      itemId: generatedItemId,
      itemCode: generatedItemCode,
      itemName: productName,
      weight: parseFloat(weight),
      price: calculatedFinalPrice,
      makingCharges: parseFloat(makingCharges) || 0,
      description: description,
      category: productCategory,
      gemstoneType: gemstoneType,
      sku: sku,
      stockQuantity: parseInt(stockQuantity),
      discount: discount ? parseFloat(discount) : 0,
      tax: tax ? parseFloat(tax) : 0,
      imageUrl: finalImageUrl, 
      purity: purity,
      city: selectedCity,
    };

    let endpoint = '';

    if (materialType === 'Gold') {
      // endpoint = 'http://localhost:8080/api/admin/gold-products';
      endpoint = 'https://sardar-jewellers.com/api/admin/gold-products';
    } else if (materialType === 'Silver') {
      // endpoint = 'http://localhost:8080/api/admin/silver-products';
      endpoint = 'https://sardar-jewellers.com/api/admin/silver-products';
    } else {
      setErrorMessage('Invalid material type selected.');
      return;
    }

    try {
      const bodyToSend = {
        product: commonProductData,
        imagePaths: [finalImageUrl],
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
        },
        body: JSON.stringify(bodyToSend),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to add product: ${errorText}`);
      }

      const createdProduct = await response.json();
      console.log('Product created successfully:', createdProduct);

      setSuccessMessage('Product created successfully! Redirecting to product list...');
      setTimeout(() => {
        setSuccessMessage(null);
        navigate('/admin/products/list');
      }, 1500);
    } catch (error: any) {
      console.error('Error creating product:', error);
      setErrorMessage(`Error creating product: ${error.message}`);
    }
  };

  return (
    <div className="flex p-8 bg-gray-100 min-h-screen">
      {/* Success Message Display */}
      {successMessage && (
        <div className="fixed top-4 right-4 bg-green-500 text-white py-3 px-6 rounded-lg shadow-lg z-50">
          {successMessage}
        </div>
      )}
      {/* Error Message Display */}
      {errorMessage && (
        <div className="fixed top-4 right-4 bg-red-500 text-white py-3 px-6 rounded-lg shadow-lg z-50">
          {errorMessage}
        </div>
      )}
      {/* Left Sidebar - Product Preview Card */}
      <div className="w-1/4 pr-8">
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h3 className="text-xl font-semibold mb-4 text-gray-800">Product Preview</h3>
          {/* Display preview image from state */}
          {previewImage || imageUrl ? (
            <img src={previewImage || imageUrl} alt="Product Preview" className="w-full h-48 object-contain mb-4 rounded-md border border-gray-200" />
          ) : (
            <div className="w-full h-48 bg-gray-200 flex items-center justify-center text-gray-500 rounded-md mb-4">
              No Image
            </div>
          )}
          <h3 className="text-lg font-semibold text-gray-900">{productName || 'Jewelry Item Name'}</h3>
          <p className="text-sm text-gray-600 mb-2">Category: {productCategory || 'N/A'}</p>
          <div className="flex items-baseline mb-3">
            <span className="text-red-600 font-bold text-xl">
                {formatCurrency(calculatedFinalPrice)}
            </span>
            {discount && parseFloat(discount) > 0 && <span className="ml-2 text-sm text-red-500">({discount}% Off)</span>}
          </div>

          <div className="mb-3">
            <h4 className="font-medium text-gray-700 mb-1">Sizes:</h4>
            <div className="flex flex-wrap gap-2">
              {(productCategory === 'Rings' ? ringSizes : necklaceBraceletLengths).map((size) => (
                <span
                  key={size}
                  className={`px-3 py-1 border rounded-full text-sm cursor-pointer
                    ${selectedSizes.includes(size) ? 'bg-yellow-600 text-white' : 'border-gray-300 text-gray-700 bg-gray-50'}
                  `}
                >
                  {size}
                </span>
              ))}
              {selectedSizes.length === 0 && (
                 <span className="text-gray-400 text-sm">Select sizes above</span>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons in Preview Panel */}
        <div className="flex space-x-4">
          <button
            type="submit"
            form="create-product-form"
            className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-3 px-4 rounded-lg transition duration-200 text-center"
          >
            Create Product
          </button>
          <button
            type="button"
            className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-4 rounded-lg transition duration-200 text-center"
            onClick={() => navigate('/admin/products/list')}
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Main Content - Product Form */}
      <div className="w-3/4">
        <form onSubmit={handleSubmit} id="create-product-form">
          {/* Add Product Photo */}
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Add Product Photo</h2>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-10 text-center cursor-pointer hover:border-gray-400 transition duration-200" onDragOver={handleDragOver} onDrop={handleDrop} onClick={() => document.getElementById('fileInput')?.click()} >
              {(previewImage || imageUrl) ? (
                <img src={previewImage || imageUrl} alt="Uploaded Preview" className="max-h-60 mx-auto object-contain mb-4" />
              ) : (
                <>
                  <CloudUpload className="mx-auto w-16 h-16 text-gray-400 mb-4" />
                  <p className="text-lg text-gray-600 mb-2"> Drop your images here, or <span className="text-blue-600 font-medium">click to browse</span> </p>
                  <p className="text-sm text-gray-500"> Recommended: 1200 x 1200 (1:1) or 1600 x 1200 (4:3) recommended. PNG, JPG and GIF files are allowed </p>
                </>
              )}
              {/* input to select files */}
              <input id="fileInput" type="file" accept="image/png, image/jpeg, image/gif" className="hidden" onChange={handleFileChangeForPreview} />
            </div>
            {/* Input for Image URL - now it's an alternative to file upload */}
            <div className="mt-4">
                <label htmlFor="imageUrl" className="block text-sm font-medium text-gray-700 mb-1">Image URL (Optional, if not uploading a file)</label>
                <input
                    type="url"
                    id="imageUrl"
                    className="w-full border-gray-300 rounded-md shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                    placeholder="e.g., https://example.com/product-image.jpg"
                    value={imageUrl}
                    onChange={(e) => {
                      setImageUrl(e.target.value);
                      setSelectedFile(null); 
                      setPreviewImage(e.target.value); 
                    }}
                    disabled={!!selectedFile} 
                />
            </div>
          </div>

          {/* Product Information */}
          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Product Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="productName" className="block text-sm font-medium text-gray-700 mb-1">Product Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  id="productName"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                  placeholder="e.g., Elegant Gold Necklace"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="materialType" className="block text-sm font-medium text-gray-700 mb-1">Material Type <span className="text-red-500">*</span></label>
                <select
                  id="materialType"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                  value={materialType}
                  onChange={(e) => {
                    setMaterialType(e.target.value);
                    setGemstoneType('');
                  }}
                  required
                >
                  <option value="">Select Material</option>
                  <option value="Gold">Gold</option>
                  <option value="Silver">Silver</option>
                </select>
              </div>
              <div>
                <label htmlFor="gemstoneType" className="block text-sm font-medium text-gray-700 mb-1">Gemstone Type {materialType === 'Gold' && <span className="text-red-500">*</span>}</label>
                <input
                  type="text"
                  id="gemstoneType"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                  placeholder="e.g., Diamond, Ruby, Sapphire (Required for Gold)"
                  value={gemstoneType}
                  onChange={(e) => setGemstoneType(e.target.value)}
                  required={materialType === 'Gold'}
                />
              </div>
              <div>
                <label htmlFor="productCategory" className="block text-sm font-medium text-gray-700 mb-1">Product Category <span className="text-red-500">*</span></label>
                <select
                  id="productCategory"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                  value={productCategory}
                  onChange={(e) => setProductCategory(e.target.value)}
                  required
                >
                  <option value="">Select Category</option>
                  {productCategories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="weight" className="block text-sm font-medium text-gray-700 mb-1">Weight (in grams) <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  id="weight"
                  step="0.01"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                  placeholder="e.g., 5.25"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">Gender <span className="text-red-500">*</span></label>
                <select
                  id="gender"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="Unisex">Unisex</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              <div>
                <label htmlFor="purity" className="block text-sm font-medium text-gray-700 mb-1">Purity <span className="text-red-500">*</span></label>
                <select
                  id="purity"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                  value={purity}
                  onChange={(e) => setPurity(e.target.value)}
                  required
                  disabled={!materialType}
                >
                  <option value="">Select Purity</option>
                  {materialType === 'Gold' && (
                    <>
                      <option value="18K">18K</option>
                      <option value="22K">22K</option>
                      <option value="24K">24K</option>
                    </>
                  )}
                  {materialType === 'Silver' && (
                    <>
                      <option value="925">925 (Sterling Silver)</option>
                      <option value="999">999 (Fine Silver)</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Product Description <span className="text-red-500">*</span></label>
              <textarea
                id="description"
                rows={4}
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Detailed description of the product, including features, materials, and design elements."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
              ></textarea>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Sizes Available</label>
              <div className="flex flex-wrap gap-2">
                {(productCategory === 'Rings' ? ringSizes : necklaceBraceletLengths).map((size) => (
                  <button
                    key={size}
                    type="button"
                    className={`px-4 py-2 border rounded-full text-sm font-medium transition duration-200
                      ${selectedSizes.includes(size) ? 'bg-yellow-600 text-white shadow-md' : 'border-gray-300 text-gray-700 bg-gray-50 hover:bg-gray-100'}
                    `}
                    onClick={() => handleSizeToggle(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="sku" className="block text-sm font-medium text-gray-700 mb-1">SKU (Stock Keeping Unit) <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  id="sku"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                  placeholder="e.g., GOLD-NECK-001"
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  required
                />
              </div>
              <div>
                <label htmlFor="stockQuantity" className="block text-sm font-medium text-gray-700 mb-1">Stock Quantity <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  id="stockQuantity"
                  className="w-full border-gray-300 rounded-md shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                  placeholder="e.g., 100"
                  value={stockQuantity}
                  onChange={(e) => setStockQuantity(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {tags.map((tag, index) => (
                  <span key={index} className="flex items-center bg-gray-200 px-3 py-1 rounded-full text-sm">
                    {tag}
                    <button type="button" className="ml-2 text-gray-600 hover:text-gray-800" onClick={() => handleRemoveTag(tag)}>
                      &times;
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                id="tags"
                className="w-full border-gray-300 rounded-md shadow-sm focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Add tags (e.g., vintage, handmade, unique), press Enter to add"
                value={currentTagInput}
                onChange={(e) => setCurrentTagInput(e.target.value)}
                onKeyPress={handleAddTag}
              />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Pricing & Live Rate Settings</h2>
          
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="makingCharges" className="block text-sm font-medium text-gray-700 mb-1">Making Charges (Optional)</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path d="M10.164 13.914L10 13.75a1 1 0 010-2h4a1 1 0 010 2h-4.164zM16 11.75a1 1 0 010-2h2.5c.276 0 .5.5s-.224.5-.5.5H16zm0-4a1 1 0 010-2h3c.276 0 .5.224.5.5s-.224.5-.5.5H16zM13 18.75a1 1 0 010-2h-3.5a.5.5 0 00-.5.5v2a.5.5 0 00.5.5H13z" />
                      <path fillRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1 5a1 1 0 00-1 1v2.25c0 .276.224.5.5.5h6.5a.5.5 0 01.5.5v1.5a.5.5 0 01-.5.5h-5.5a1 1 0 00-1 1v2.25a1 1 0 001 1h4a1 1 0 001-1V12.5a.5.5 0 00-.5-.5h-6.5a.5.5 0 01-.5-.5V10c0-.276-.224-.5-.5-.5H8a1 1 0 00-1 1v2.25c0 .276.224.5.5.5h3.5a.5.5 0 01.5.5v1.5a.5.5 0 01-.5.5H8a1 1 0 00-1 1v.75a1 1 0 001 1h4a2 2 0 002-2V8a2 2 0 00-2-2H8z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <input
                    type="number"
                    id="makingCharges"
                    step="0.01"
                    className="flex-1 block w-full rounded-none rounded-r-md border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
                    placeholder="e.g., 500.00"
                    value={makingCharges}
                    onChange={(e) => setMakingCharges(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="discount" className="block text-sm font-medium text-gray-700 mb-1">Discount (%)</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                    <Percent className="h-5 w-5" />
                  </span>
                  <input
                    type="number"
                    id="discount"
                    step="0.01"
                    className="flex-1 block w-full rounded-none rounded-r-md border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
                    placeholder="e.g., 10.00"
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="tax" className="block text-sm font-medium text-gray-700 mb-1">Tax (%)</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                    <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'%3E%3C/path%3E%3Cpolyline points='14 2 14 8 20 8'%3E%3C/polyline%3E%3Cline x1='12' y1='17' x2='12' y2='11'%3E%3C/line%3E%3Cline x1='9' y1='14' x2='15' y2='14'%3E%3C/line%3E%3C/svg%3E" alt="Tax Icon" className="h-5 w-5" />
                  </span>
                  <input
                    type="number"
                    id="tax"
                    step="0.01"
                    className="flex-1 block w-full rounded-none rounded-r-md border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
                    placeholder="e.g., 5.00"
                    value={tax}
                    onChange={(e) => setTax(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="mt-6 p-4 bg-gray-50 rounded-md border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Calculated Product Price:</h3>
                <p className="text-3xl font-extrabold text-green-700">
                    {formatCurrency(calculatedFinalPrice)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                    (Based on backend live rate, weight, making charges, discount, and tax)
                </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProduct;